import React from 'react';
import {
  normalizeLayout,
  writeLayout,
  type DashboardLayout,
  type DashboardWidgetChoice,
} from '../dashboardWidgets';

type DashboardWidgetEditorProps = {
  surface: string;
  language: 'en' | 'es';
  widgets: DashboardWidgetChoice[];
  hidden: ReadonlySet<string>;
  order?: readonly string[];
  onChange: (layout: DashboardLayout) => void;
};

const reorder = (order: string[], fromId: string, toId: string): string[] => {
  if (!fromId || !toId || fromId === toId) {
    return order;
  }
  const next = order.filter((id) => id !== fromId);
  const index = next.indexOf(toId);
  if (index < 0) {
    return order;
  }
  next.splice(index, 0, fromId);
  return next;
};

const DashboardWidgetEditor: React.FC<DashboardWidgetEditorProps> = ({
  surface,
  language,
  widgets,
  hidden,
  order,
  onChange,
}) => {
  const [open, setOpen] = React.useState(false);
  const [draftOrder, setDraftOrder] = React.useState<string[]>([]);
  const [draftHidden, setDraftHidden] = React.useState<Set<string>>(new Set());
  const [overId, setOverId] = React.useState('');
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const dragId = React.useRef('');
  const spanish = language === 'es';

  const begin = () => {
    const current = normalizeLayout(widgets, {
      order: order && order.length ? [...order] : widgets.map((widget) => widget.id),
      hidden: [...hidden],
    });
    setDraftOrder(current.order);
    setDraftHidden(new Set(current.hidden));
    setOverId('');
    setOpen(true);
  };

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    const onPointer = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const move = (id: string, direction: -1 | 1) => {
    setDraftOrder((current) => {
      const index = current.indexOf(id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }
      const next = current.slice();
      const [row] = next.splice(index, 1);
      next.splice(nextIndex, 0, row);
      return next;
    });
  };

  const save = () => {
    const next = normalizeLayout(widgets, { order: draftOrder, hidden: [...draftHidden] });
    writeLayout(surface, next);
    onChange(next);
    setOpen(false);
  };

  const rows = draftOrder
    .map((id) => widgets.find((widget) => widget.id === id))
    .filter((widget): widget is DashboardWidgetChoice => Boolean(widget));

  return (
    <div className="dashboard-widget-editor" ref={anchorRef}>
      <button
        type="button"
        className={`compliance-calendar-launch${open ? ' is-on' : ''}`}
        aria-expanded={open}
        aria-pressed={open}
        aria-label={spanish ? 'Editar tarjetas' : 'Edit widgets'}
        onClick={() => (open ? setOpen(false) : begin())}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="9" cy="7" r="2.1" fill="currentColor" />
          <circle cx="15" cy="12" r="2.1" fill="currentColor" />
          <circle cx="8" cy="17" r="2.1" fill="currentColor" />
        </svg>
        <span className="dashboard-widget-tip" aria-hidden="true">
          {spanish ? 'Arrastrar, mostrar u ocultar tarjetas' : 'Drag, show, or hide cards'}
        </span>
      </button>
      {open ? (
        <div className="dashboard-widget-panel" role="dialog" aria-label={spanish ? 'Editar tarjetas' : 'Edit widgets'}>
          <p>
            {spanish
              ? 'Arrastre las tarjetas o use las flechas. Guardar las deja en su cuenta.'
              : 'Drag the cards, or use the arrows. Save keeps this on your account.'}
          </p>
          <ul>
            {rows.map((widget, index) => {
              const label = spanish ? widget.es : widget.en;
              const shown = !draftHidden.has(widget.id);
              return (
                <li
                  key={widget.id}
                  className={`dashboard-widget-row${overId === widget.id ? ' is-over' : ''}${shown ? '' : ' is-hidden'}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setOverId(widget.id);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const from = event.dataTransfer.getData('text/plain') || dragId.current;
                    setDraftOrder((current) => reorder(current, from, widget.id));
                    dragId.current = '';
                    setOverId('');
                  }}
                >
                  <button
                    type="button"
                    className="dashboard-widget-grip"
                    draggable
                    aria-label={spanish ? `Arrastrar ${label}` : `Drag ${label}`}
                    onDragStart={(event) => {
                      dragId.current = widget.id;
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', widget.id);
                    }}
                    onDragEnd={() => {
                      dragId.current = '';
                      setOverId('');
                    }}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="9" cy="7" r="1.3" fill="currentColor" />
                      <circle cx="15" cy="7" r="1.3" fill="currentColor" />
                      <circle cx="9" cy="12" r="1.3" fill="currentColor" />
                      <circle cx="15" cy="12" r="1.3" fill="currentColor" />
                      <circle cx="9" cy="17" r="1.3" fill="currentColor" />
                      <circle cx="15" cy="17" r="1.3" fill="currentColor" />
                    </svg>
                    <span className="dashboard-widget-tip" aria-hidden="true">
                      {spanish ? 'Arrastrar para cambiar el orden' : 'Drag to change the order'}
                    </span>
                  </button>
                  <label>
                    <input
                      type="checkbox"
                      checked={shown}
                      onChange={(event) => {
                        setDraftHidden((current) => {
                          const next = new Set(current);
                          if (event.target.checked) {
                            next.delete(widget.id);
                          } else {
                            next.add(widget.id);
                          }
                          return next;
                        });
                      }}
                    />
                    <span>{label}</span>
                  </label>
                  <button
                    type="button"
                    className="dashboard-widget-move"
                    aria-label={spanish ? `Subir ${label}` : `Move ${label} up`}
                    disabled={index === 0}
                    onClick={() => move(widget.id, -1)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 14l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="dashboard-widget-tip" aria-hidden="true">
                      {spanish ? 'Subir' : 'Move up'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dashboard-widget-move"
                    aria-label={spanish ? `Bajar ${label}` : `Move ${label} down`}
                    disabled={index === rows.length - 1}
                    onClick={() => move(widget.id, 1)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 10l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="dashboard-widget-tip" aria-hidden="true">
                      {spanish ? 'Bajar' : 'Move down'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="dashboard-widget-actions">
            <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={save}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 12.5 10 16.5 18 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {spanish ? 'Guardar' : 'Save'}
            </button>
            <button
              type="button"
              className="dashboard-btn"
              onClick={() => {
                setDraftOrder(widgets.map((widget) => widget.id));
                setDraftHidden(new Set());
              }}
            >
              {spanish ? 'Restablecer' : 'Reset'}
            </button>
            <button type="button" className="dashboard-btn" onClick={() => setOpen(false)}>
              {spanish ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DashboardWidgetEditor;
